import { QueryLanguage } from "./QueryLanguage.js";
import { EntityFieldsWithHoistablesMarker } from "../markers/EntityFieldsWithHoistablesMarker.js";
import { MarkerMerger } from "../core/MarkerMerger.js";
import { EntitySubTreeMarker } from "../markers/EntitySubTreeMarker.js";
import { EntityListSubTreeMarker } from "../markers/EntityListSubTreeMarker.js";
import { EntityFieldMarkersContainer } from "../markers/EntityFieldMarkersContainer.js";
import { FieldMarker } from "../markers/FieldMarker.js";
import { HasOneRelationMarker } from "../markers/HasOneRelationMarker.js";
import { HasManyRelationMarker } from "../markers/HasManyRelationMarker.js";
import { TreeParameterMerger } from "../core/TreeParameterMerger.js";
import { assertNever } from "../utils/assertNever.js";
class MarkerFactory {
  static createSubTreeMarker(qualifiedParams, key, Marker, fields, environment) {
    let entityFields;
    if (fields instanceof EntityFieldsWithHoistablesMarker) {
      qualifiedParams = TreeParameterMerger.mergeInParentEntity(qualifiedParams, key, fields.parentReference);
      entityFields = fields.fields;
    } else {
      entityFields = fields;
    }
    const subTree = new Marker(
      qualifiedParams,
      this.wrapRelativeEntityFieldMarkers(
        qualifiedParams.hasOneRelationPath,
        environment,
        MarkerMerger.mergeInSystemFields(entityFields)
      ),
      environment
    );
    if (fields instanceof EntityFieldsWithHoistablesMarker) {
      return new EntityFieldsWithHoistablesMarker(
        this.createEntityFieldMarkersContainer(void 0),
        MarkerMerger.mergeSubTreeMarkers(fields.subTrees, /* @__PURE__ */ new Map([[subTree.placeholderName, subTree]])),
        void 0
      );
    }
    return new EntityFieldsWithHoistablesMarker(
      this.createEntityFieldMarkersContainer(void 0),
      /* @__PURE__ */ new Map([[subTree.placeholderName, subTree]]),
      void 0
    );
  }
  static createEntitySubTreeMarker(entity, fields, environment) {
    const qualifiedSingleEntity = QueryLanguage.desugarQualifiedSingleEntity(entity, environment, { missingSetOnCreate: "fillAndWarn" });
    return this.createSubTreeMarker(qualifiedSingleEntity, "eventListeners", EntitySubTreeMarker, fields, environment);
  }
  static createUnconstrainedEntitySubTreeMarker(entity, fields, environment) {
    return this.createSubTreeMarker(
      QueryLanguage.desugarUnconstrainedQualifiedSingleEntity(entity, environment),
      "eventListeners",
      EntitySubTreeMarker,
      fields,
      environment
    );
  }
  static createEntityListSubTreeMarker(entityList, fields, environment) {
    return this.createSubTreeMarker(
      QueryLanguage.desugarQualifiedEntityList(entityList, environment),
      "childEventListeners",
      EntityListSubTreeMarker,
      fields,
      environment
    );
  }
  static createUnconstrainedEntityListSubTreeMarker(entityList, fields, environment) {
    return this.createSubTreeMarker(
      QueryLanguage.desugarUnconstrainedQualifiedEntityList(entityList, environment),
      "childEventListeners",
      EntityListSubTreeMarker,
      fields,
      environment
    );
  }
  static createParentEntityMarker(parentEntity, fields, environment) {
    const desugared = QueryLanguage.desugarParentEntityParameters(parentEntity, environment);
    if (fields instanceof EntityFieldMarkersContainer) {
      return new EntityFieldsWithHoistablesMarker(fields, void 0, desugared);
    }
    return new EntityFieldsWithHoistablesMarker(
      fields.fields,
      fields.subTrees,
      TreeParameterMerger.mergeParentEntityParameters(fields.parentReference, desugared)
    );
  }
  static createEntityFieldsWithHoistablesMarker(fields, environment) {
    if (fields instanceof EntityFieldMarkersContainer) {
      return new EntityFieldsWithHoistablesMarker(fields, void 0, void 0);
    }
    return new EntityFieldsWithHoistablesMarker(fields.fields, fields.subTrees, void 0);
  }
  static createRelativeSingleEntityFields(field, environment, fields) {
    const relativeSingleEntity = QueryLanguage.desugarRelativeSingleEntity(field, environment);
    if (fields instanceof EntityFieldMarkersContainer) {
      return this.wrapRelativeEntityFieldMarkers(relativeSingleEntity.hasOneRelationPath, environment, fields);
    }
    relativeSingleEntity.hasOneRelationPath[relativeSingleEntity.hasOneRelationPath.length - 1] = TreeParameterMerger.mergeInParentEntity(
      relativeSingleEntity.hasOneRelationPath[relativeSingleEntity.hasOneRelationPath.length - 1],
      "eventListeners",
      fields.parentReference
    );
    return new EntityFieldsWithHoistablesMarker(
      this.wrapRelativeEntityFieldMarkers(relativeSingleEntity.hasOneRelationPath, environment, fields.fields),
      fields.subTrees,
      void 0
    );
  }
  static createRelativeEntityListFields(field, environment, fields) {
    const relativeEntityList = QueryLanguage.desugarRelativeEntityList(field, environment);
    if (fields instanceof EntityFieldMarkersContainer) {
      const hasManyRelationMarker2 = this.createHasManyRelationMarker(
        relativeEntityList.hasManyRelation,
        environment,
        fields
      );
      return this.wrapRelativeEntityFieldMarkers(
        relativeEntityList.hasOneRelationPath,
        environment.getParent(),
        this.createEntityFieldMarkersContainer(hasManyRelationMarker2)
      );
    }
    relativeEntityList.hasManyRelation = TreeParameterMerger.mergeInParentEntity(
      relativeEntityList.hasManyRelation,
      "childEventListeners",
      fields.parentReference
    );
    const hasManyRelationMarker = this.createHasManyRelationMarker(
      relativeEntityList.hasManyRelation,
      environment,
      fields.fields
    );
    return new EntityFieldsWithHoistablesMarker(
      this.wrapRelativeEntityFieldMarkers(
        relativeEntityList.hasOneRelationPath,
        environment.getParent(),
        this.createEntityFieldMarkersContainer(hasManyRelationMarker)
      ),
      fields.subTrees,
      void 0
    );
  }
  static createFieldMarker(field, environment) {
    return this.wrapRelativeSingleField(
      field,
      environment,
      (relativeSingleField) => new FieldMarker(relativeSingleField.field, relativeSingleField.defaultValue, relativeSingleField.isNonbearing)
    );
  }
  static wrapRelativeSingleField(field, environment, getMarker) {
    const relativeSingleField = QueryLanguage.desugarRelativeSingleField(field, environment);
    return this.wrapRelativeEntityFieldMarkers(
      relativeSingleField.hasOneRelationPath,
      environment.getParent(),
      this.createEntityFieldMarkersContainer(getMarker(relativeSingleField))
    );
  }
  static wrapRelativeEntityFieldMarkers(hasOneRelationPath, environment, fields) {
    for (let i = hasOneRelationPath.length - 1; i >= 0; i--) {
      const marker = this.createHasOneRelationMarker(hasOneRelationPath[i], environment, fields);
      environment = environment.getParent();
      fields = this.createEntityFieldMarkersContainer(marker);
    }
    return fields;
  }
  static createEntityFieldMarkersContainer(marker) {
    if (marker instanceof FieldMarker) {
      return new EntityFieldMarkersContainer(
        !marker.isNonbearing,
        /* @__PURE__ */ new Map([[marker.placeholderName, marker]]),
        /* @__PURE__ */ new Map([[marker.fieldName, marker.placeholderName]])
      );
    } else if (marker instanceof HasOneRelationMarker || marker instanceof HasManyRelationMarker) {
      return new EntityFieldMarkersContainer(
        !marker.parameters.isNonbearing,
        /* @__PURE__ */ new Map([[marker.placeholderName, marker]]),
        /* @__PURE__ */ new Map([[marker.parameters.field, marker.placeholderName]])
      );
    } else if (marker === void 0) {
      return new EntityFieldMarkersContainer(false, /* @__PURE__ */ new Map(), /* @__PURE__ */ new Map());
    } else {
      return assertNever();
    }
  }
  static createHasOneRelationMarker(hasOneRelation, environment, fields) {
    return new HasOneRelationMarker(
      {
        ...hasOneRelation,
        setOnCreate: hasOneRelation.reducedBy ? TreeParameterMerger.mergeSetOnCreate(hasOneRelation.setOnCreate || {}, hasOneRelation.reducedBy) : hasOneRelation.setOnCreate
      },
      MarkerMerger.mergeInSystemFields(fields),
      environment
    );
  }
  static createHasManyRelationMarker(hasManyRelation, environment, fields) {
    return new HasManyRelationMarker(hasManyRelation, MarkerMerger.mergeInSystemFields(fields), environment);
  }
}
export {
  MarkerFactory
};
//# sourceMappingURL=MarkerFactory.js.map
